import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Package } from 'lucide-react'
import { useProducts } from '@/hooks/useProducts'
import type { Product } from '@/types'

interface RelatedProductsProps {
  currentProduct: Product
  categorySlug: string
}

export function RelatedProducts({ currentProduct, categorySlug }: RelatedProductsProps) {
  const { data: products, isLoading } = useProducts({ categorySlug })

  // Filter out current product and limit to 3
  const relatedProducts = products
    ?.filter((p) => p.id !== currentProduct.id)
    .slice(0, 3)

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="card animate-pulse">
            <div className="aspect-[4/3] bg-secondary-200" />
            <div className="p-4 space-y-2">
              <div className="h-4 bg-secondary-200 rounded w-3/4" />
              <div className="h-3 bg-secondary-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (!relatedProducts || relatedProducts.length === 0) return null

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-secondary-900">Related Products</h3>
        <Link
          to={`/products/${categorySlug}`}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
        >
          View All
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {relatedProducts.map((product, index) => {
          const primaryImage = product.images?.find((img) => img.is_primary) || product.images?.[0]

          return (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={`/products/${categorySlug}/${product.slug}`}
                className="group block card-hover"
              >
                <div className="aspect-[4/3] bg-white overflow-hidden">
                  {primaryImage ? (
                    <img
                      src={primaryImage.image_url}
                      alt={primaryImage.alt_text || product.name}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-10 h-10 text-secondary-300" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <span className="text-xs font-mono text-secondary-400">
                    {product.code}
                  </span>
                  <h4 className="font-medium text-secondary-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {product.name}
                  </h4>
                </div>
              </Link>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}
